import { useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Form,
  InputGroup,
  ListGroup,
} from "react-bootstrap";
import SubmitButton from "../../../components/SubmitButton";
import { SlantCandidate, stepErrorMessage, useSlantCandidates } from "./api";

/**
 * Step 2 — Find the Slant household.
 *
 * A search box drives a read of `slant-candidates` (ranked best-first). The
 * advisor selects one candidate; if the candidates span more than one book the
 * advisor must also pick a book to DISAMBIGUATE which household they mean.
 * Confirming hands the chosen Slant household id UP to the wizard, which holds
 * it in React state — the book is only a local disambiguation aid and is NOT
 * sent on the switch, so it is never threaded out of this step.
 */
const SwitchToSlantStep2 = ({
  householdId,
  defaultQuery,
  onPicked,
}: {
  householdId: number;
  defaultQuery: string;
  /** Hand the chosen Slant household up to the wizard. */
  onPicked: (slantHouseholdId: string) => void;
}) => {
  const [query, setQuery] = useState(defaultQuery);
  // The terms actually searched (committed on Search), so the query is stable
  // and does not refire on every keystroke.
  const [searchedName, setSearchedName] = useState<string | null>(null);
  const [selectedHouseholdId, setSelectedHouseholdId] = useState<string | null>(
    null,
  );
  const [selectedBookId, setSelectedBookId] = useState<string | null>(null);

  const search = useSlantCandidates(householdId, {
    name: searchedName ?? undefined,
  });

  const candidates: SlantCandidate[] = useMemo(
    () => search.data?.data.candidates ?? [],
    [search.data],
  );

  const selectedCandidate = useMemo(
    () => candidates.find((c) => c.id === selectedHouseholdId) ?? null,
    [candidates, selectedHouseholdId],
  );

  // Offer a book picker whenever the ranked results span more than one distinct
  // book so the advisor disambiguates.
  const bookOptions = useMemo(
    () =>
      Array.from(
        new Set(
          candidates
            .map((c) => c.book_id)
            .filter((b): b is string => typeof b === "string"),
        ),
      ),
    [candidates],
  );
  const needsBookPick = bookOptions.length > 1;

  const onSearch = () => {
    setSelectedHouseholdId(null);
    setSelectedBookId(null);
    setSearchedName(query.trim());
  };

  const onSelectCandidate = (candidate: SlantCandidate) => {
    setSelectedHouseholdId(candidate.id);
    setSelectedBookId(candidate.book_id ?? null);
  };

  const canSubmit =
    selectedCandidate !== null && (!needsBookPick || selectedBookId !== null);

  const onConfirmPick = () => {
    if (selectedCandidate === null) {
      return;
    }
    onPicked(selectedCandidate.id);
  };

  return (
    <div>
      <h5>Find the household in Slant</h5>
      <p className="text-muted">
        Search Slant for the matching household, then pick the right result.
      </p>

      <InputGroup className="mb-3">
        <Form.Control
          aria-label="Search Slant households"
          placeholder="Search Slant households"
          value={query}
          onChange={(event) => setQuery(event.currentTarget.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              onSearch();
            }
          }}
        />
        <Button
          variant="outline-primary"
          onClick={onSearch}
          disabled={search.isFetching || query.trim().length === 0}
        >
          {search.isFetching ? "Searching…" : "Search"}
        </Button>
      </InputGroup>

      {search.isError && (
        <Alert variant="danger">
          {stepErrorMessage(search.error, "Failed to search Slant households.")}
        </Alert>
      )}

      {search.isSuccess && candidates.length === 0 && (
        <Alert variant="warning">
          No Slant households matched. Adjust your search and try again.
        </Alert>
      )}

      {candidates.length > 0 && (
        <ListGroup className="mb-3">
          {candidates.map((candidate, index) => (
            <ListGroup.Item
              key={candidate.id}
              action
              active={candidate.id === selectedHouseholdId}
              onClick={() => onSelectCandidate(candidate)}
            >
              <div className="d-flex justify-content-between">
                <span>{candidate.name}</span>
                {index === 0 && (
                  <Badge bg="success" pill>
                    Best match
                  </Badge>
                )}
              </div>
              <small className="text-muted">
                {candidate.book_id ? `Book ${candidate.book_id} · ` : ""}
                {candidate.ratio}% name match
              </small>
            </ListGroup.Item>
          ))}
        </ListGroup>
      )}

      {selectedCandidate !== null && needsBookPick && (
        <Form.Group className="mb-3">
          <Form.Label>Book</Form.Label>
          <Form.Select
            aria-label="Select book"
            value={selectedBookId ?? ""}
            onChange={(event) =>
              setSelectedBookId(event.currentTarget.value || null)
            }
          >
            <option value="">Select a book…</option>
            {bookOptions.map((bookId) => (
              <option key={bookId} value={bookId}>
                {bookId}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      )}

      <div className="d-flex justify-content-end">
        <SubmitButton
          label="Use this household"
          onClick={onConfirmPick}
          isSubmitting={false}
          disabled={!canSubmit}
        />
      </div>
    </div>
  );
};

export default SwitchToSlantStep2;
